import { useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useSetAtom } from 'jotai';
import useLocationMock from './useLocationMock';
import locationStateAtom from './locationStateBridge';
import { _navActionRef, _wasPopNavigation } from './navStateBridge';
if (typeof window !== 'undefined') {
  // Prevent HMR from accumulating multiple listeners: clean up the previous
  // registration before adding a new one.
  if (window.__useHistoryMockCleanup__) {
    window.__useHistoryMockCleanup__();
  }
  const _popstateHandler = () => {
    _navActionRef.current = 'POP';
    _wasPopNavigation.current = true;
  };
  window.addEventListener('popstate', _popstateHandler);
  const _origPushState = window.history.pushState.bind(window.history);
  const _origReplaceState = window.history.replaceState.bind(window.history);
  window.history.pushState = function (...args) {
    _navActionRef.current = 'PUSH';
    _wasPopNavigation.current = false;
    return _origPushState(...args);
  };
  window.history.replaceState = function (...args) {
    const prevAction = _navActionRef.current;
    _navActionRef.current = 'REPLACE';
    if (prevAction !== 'POP') {
      _wasPopNavigation.current = false;
    }
    return _origReplaceState(...args);
  };
  window.__useHistoryMockCleanup__ = () => {
    window.removeEventListener('popstate', _popstateHandler);
    window.history.pushState = _origPushState;
    window.history.replaceState = _origReplaceState;
  };
}
const useHistoryMock = () => {
  const history = useRef({});
  const location = useLocationMock();
  history.current.location = location;
  const {
    pathname
  } = location || {};
  const {
    back,
    push,
    replace
  } = useRouter();
  history.current.back = back;
  const setLocationState = useSetAtom(locationStateAtom);

  // Wrap Next.js push to support React Router v5's history.push(path, state)
  // and history.push({ pathname, search, hash, state }) signatures.
  history.current.push = useCallback((to, state) => {
    _navActionRef.current = 'PUSH';
    history.current.action = 'PUSH';
    if (typeof to === 'string') {
      setLocationState(state ?? null);
      push(to);
    } else if (to && typeof to === 'object') {
      const {
        pathname: toPathname,
        search,
        hash,
        state: toState
      } = to;
      // State can come from the object or the second argument (object form
      // takes precedence per React Router v5 behavior)
      setLocationState(state ?? toState ?? null);
      const searchWithPrefix = search && !search.startsWith('?') ? `?${search}` : search ?? '';
      push(`${toPathname ?? pathname ?? ''}${searchWithPrefix}${hash ?? ''}`);
    }
  }, [pathname, push, setLocationState]);

  // Add goBack function - equivalent to window.history.back() in React Router v5
  history.current.goBack = back;

  // history.go(n) - equivalent to window.history.go(n)
  history.current.go = useCallback(n => {
    if (typeof window !== 'undefined') {
      window.history.go(n);
    }
  }, []);
  history.current.action = _navActionRef.current;
  const replaceMock = useCallback((newLocation, options = {}) => {
    if (!newLocation) {
      return;
    }
    _navActionRef.current = 'REPLACE';
    history.current.action = 'REPLACE';

    // React Router v5 history.replace() never scrolls to the top of the
    // page, but Next.js router.replace() scrolls by default.  Default to
    // scroll: false to match React Router v5 semantics. Callers can still
    // opt-in via { scroll: true } when explicit scrolling is desired.
    const replaceOptions = {
      scroll: options.scroll ?? false
    };
    if (typeof newLocation === 'string') {
      replace(newLocation, replaceOptions);
    } else {
      const {
        pathname: newPathname,
        search: search_0
      } = newLocation;
      const searchWithPrefix_0 = search_0 && !search_0?.startsWith('?') ? '?' + search_0 : search_0 ?? '';
      replace(`${newPathname ?? pathname ?? ''}${searchWithPrefix_0}`, replaceOptions);
    }
  }, [pathname, replace]);
  history.current.replace = replaceMock;
  const listen = useCallback(callbackFn => {
    'use client';

    const hash_0 = window?.location?.hash;
    const handler = () => {
      if (!hash_0) {
        callbackFn();
      }
    };
    window.addEventListener('hashchange', handler);

    // Return an unlisten function for cleanup, matching React Router v5 API
    return () => {
      window.removeEventListener('hashchange', handler);
    };
  }, []);
  history.current.listen = listen;
  return history.current;
};
export default useHistoryMock;